import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import './LoginPage.css';

type Step = 'email' | 'verify';

export default function LoginPage() {
  const { user, loading, sendOtpCode, verifyOtpCode } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCountdown, setResendCountdown] = useState(0);

  useEffect(() => {
    if (!loading && user) {
      const pendingToken = localStorage.getItem('pending_invite_token');
      if (pendingToken) {
        localStorage.removeItem('pending_invite_token');
        navigate(`/invite/${pendingToken}`, { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setTimeout(() => setResendCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  async function handleSendCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    setSubmitting(true);
    const { error: err } = await sendOtpCode(email.trim());
    setSubmitting(false);
    if (err) {
      setError(err.message);
      return;
    }
    setStep('verify');
    setResendCountdown(60);
  }

  async function handleVerifyCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!code.trim() || code.trim().length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }
    setSubmitting(true);
    const { error: err } = await verifyOtpCode(email.trim(), code.trim());
    setSubmitting(false);
    if (err) {
      setError(err.message);
      return;
    }
  }

  async function handleResend() {
    setError(null);
    setSubmitting(true);
    const { error: err } = await sendOtpCode(email.trim());
    setSubmitting(false);
    if (err) {
      setError(err.message);
      return;
    }
    setResendCountdown(60);
    setCode('');
  }

  if (loading) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-card">
            <div className="animate-pulse text-center">
              <p>Loading...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          {step === 'email' ? (
            <>
              <h1>Welcome Back</h1>
              <p className="login-subtitle">
                Sign in to access your dashboard and team insights.
              </p>

              <form className="login-form" onSubmit={handleSendCode}>
                <div className="form-group">
                  <label htmlFor="email">EMAIL ADDRESS</label>
                  <input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={submitting}
                    autoComplete="email"
                    autoFocus
                    required
                  />
                </div>

                {error && <p className="form-error">{error}</p>}

                <button
                  type="submit"
                  className="btn btn-primary submit-btn"
                  disabled={submitting}
                >
                  {submitting ? 'Sending...' : 'Send Verification Code'}
                </button>

                <p className="login-note">
                  We'll send you a 6-digit code to sign in—no password needed.
                </p>
              </form>

              <div className="login-divider">
                <span>New to E.Q.U.I.P. 360?</span>
              </div>

              <Link to="/signup" className="btn btn-secondary signup-link">
                Create an Account
              </Link>
            </>
          ) : (
            <>
              <div className="email-sent-icon">📧</div>
              <h1>Check Your Email</h1>
              <p className="login-subtitle">
                We sent a 6-digit code to <strong>{email}</strong>. Enter it below to sign in.
              </p>

              <form className="login-form" onSubmit={handleVerifyCode}>
                <div className="form-group">
                  <label htmlFor="code">VERIFICATION CODE</label>
                  <input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    disabled={submitting}
                    className="otp-input"
                    autoFocus
                    maxLength={6}
                    required
                  />
                </div>

                {error && <p className="form-error">{error}</p>}

                <button
                  type="submit"
                  className="btn btn-primary submit-btn"
                  disabled={submitting || code.length !== 6}
                >
                  {submitting ? 'Verifying...' : 'Verify Code'}
                </button>

                <div className="email-tips">
                  <p className="tip-title">Didn't receive it?</p>
                  <ul>
                    <li>Check your <strong>spam or junk folder</strong></li>
                    <li>Make sure you used the correct email</li>
                    <li>
                      {resendCountdown > 0 ? (
                        <>Resend available in {resendCountdown}s</>
                      ) : (
                        <button
                          type="button"
                          className="link-button"
                          onClick={handleResend}
                          disabled={submitting}
                        >
                          Resend verification code
                        </button>
                      )}
                    </li>
                  </ul>
                </div>
              </form>

              <button
                type="button"
                className="back-link"
                onClick={() => { setStep('email'); setError(null); setCode(''); }}
              >
                ← Use a different email
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
